import { I18nextProvider, useTranslation } from 'react-i18next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createContext, useCallback, useContext, ReactNode, useEffect } from 'react';
import { TFunction } from 'i18next';
import useLocalStorage from './useLocalstorage';

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from '../../../language/en.json';
import hu from '../../../language/hu.json';
import jp from '../../../language/jp.json';
import haw from '../../../language/haw.json';
import { TipTapContentContentItem, TipTapParagraphContentItem } from '../../openapi/model';
import { LocalStorageKeys } from '../common/LocalStorageKeys';

export const LANGUAGE_OPTIONS: { [key: string]: string } = {
    en: 'English',
    // hu: 'Hungarian',
    // jp: 'Japanese',
    // haw: 'Hawaiian',
};

//  RFC 5646 to LANGUAGE_OPTIONS keys
export const LANGCODE_TO_LANGUAGE: { [key: string]: string } = {
    'en-US': 'en',
    'en-GB': 'en',
    'hu-HU': 'hu',
    hu: 'hu', // Hungarian can be just 'hu'
    'ja-JP': 'jp',
    ja: 'jp', // Japanese can be just 'ja'
    'haw-US': 'haw',
    haw: 'haw', // ISO 639-2/3 code for Hawaiian
};

// Origin of the fireknife-i18n tool that embeds this app in an iframe for live
// translation editing. postMessage language-change events are only honored from
// a trusted origin, and language files are only fetched from it. In dev mode we
// also accept localhost / 127.0.0.1 so the locally-served i18n tool can drive
// the embedded widget.
const I18N_TOOL_ORIGIN = 'https://fireknife18n.donebox.hu';

function isTrustedI18nOrigin(origin: string): boolean {
    if (origin === I18N_TOOL_ORIGIN) return true;
    if (!import.meta.env.DEV) return false;
    try {
        const host = new URL(origin).hostname;
        return host === 'localhost' || host === '127.0.0.1';
    } catch {
        return false;
    }
}

i18n.use(initReactI18next).init({
    resources: {
        en: {
            translation: en,
        },
        jp: {
            translation: jp,
        },
        hu: {
            translation: hu,
        },
        haw: {
            translation: haw,
        },
    },
    lng: 'en', // default language to use.
    // Hawaiian ships as an empty bundle for now; missing keys fall back to en
    // until the translations are filled in via the fireknife-i18n tool.
    fallbackLng: 'en',
    compatibilityJSON: 'v4',
});

const queryClient = new QueryClient();

export type II18nLanguageObj =
    | {
          [key: string]: unknown;
      }
    | undefined;

interface ILangContext {
    language: string;
    setLanguage: (lang: string) => void;
    getI18n: (
        englishString: string | TipTapContentContentItem | TipTapParagraphContentItem | undefined,
        languageObj: II18nLanguageObj,
        index?: number | undefined,
    ) => string;
    t: TFunction;
}

const LangContext = createContext<ILangContext>({
    language: 'en',
    setLanguage: (_lang: string) => {},
    getI18n: (
        _englishString: string | TipTapContentContentItem | TipTapParagraphContentItem | undefined,
        _languageObj: { [key: string]: unknown } | undefined,
        _index?: number | undefined,
    ) => '',
    t: ((key: string) => {
        console.warn(`Translation for ${key} not found`);
        return key;
    }) as TFunction,
});

function getBrowserLanguage() {
    const lang: string = navigator.language || navigator.userLanguage;
    if (LANGCODE_TO_LANGUAGE[lang as keyof typeof LANGCODE_TO_LANGUAGE]) {
        return LANGCODE_TO_LANGUAGE[lang as keyof typeof LANGCODE_TO_LANGUAGE];
    }
    return 'en';
}

export function LangContextProvider({ children }: { children: ReactNode }) {
    const [language, setLanguage] = useLocalStorage<string>(LocalStorageKeys.LANG, () =>
        getBrowserLanguage(),
    );

    const setLanguageWithFilter = useCallback(
        (lang: string) => {
            if (!LANGUAGE_OPTIONS[lang as keyof typeof LANGUAGE_OPTIONS]) {
                console.warn(`Language ${lang} is not supported`);
                return;
            }
            setLanguage(lang);
        },
        [setLanguage],
    );

    useEffect(() => {
        if (i18n.language === language) {
            return;
        }

        i18n.changeLanguage(language)
            .then(() => {
                console.log(`Language changed to ${language}`);
            })
            .catch((err: Error | unknown) => {
                console.error(`Error changing language to ${language}: ${err}`);
            });

        // with language change, invalidate all previous queries (fetch in BG, resetQueries fetchers in FG)
        queryClient.invalidateQueries();
    }, [language]);

    useEffect(() => {
        const handler = async (event: MessageEvent) => {
            if (event.data?.type !== 'FIRESKNIFE_I18N_LANG_CHANGE') return;
            // Only trust messages from the i18n tool that embeds us.
            if (event.source !== window.parent || !isTrustedI18nOrigin(event.origin)) return;
            const {
                language: targetLang,
                languageFile,
                languageData,
            } = event.data as {
                language?: string;
                languageFile?: string;
                languageData?: Record<string, unknown>;
            };
            if (!targetLang) return;
            try {
                let data = languageData;
                if (!data && languageFile) {
                    // Resolve against the sender's origin — never fetch off-origin.
                    const fileUrl = new URL(languageFile, event.origin);
                    if (fileUrl.origin !== event.origin) return;
                    const res = await fetch(fileUrl);
                    const json = await res.json();
                    data = json.data;
                }
                if (!data) return;
                i18n.addResourceBundle(targetLang, 'translation', data, true, true);
                await i18n.changeLanguage(targetLang);
                setLanguage(targetLang);
                queryClient.invalidateQueries();
            } catch (err) {
                console.error('[widget] Failed to apply i18n language change:', err);
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [setLanguage]);

    // languageObj is an object, key is the language code, value is the translated string
    const getI18n = useCallback(
        (
            englishString:
                | string
                | TipTapContentContentItem
                | TipTapParagraphContentItem
                | undefined,
            languageObj: { [key: string]: unknown } | undefined,
            index?: number | undefined,
        ): string | TipTapContentContentItem | TipTapParagraphContentItem => {
            if (
                languageObj &&
                typeof languageObj[language] === 'string' &&
                languageObj[language].length > 0
            ) {
                return languageObj[language];
            }

            // Check if the englishString is a TipTapContentContentItem or TipTapParagraphContentItem
            if (
                languageObj &&
                languageObj[language] &&
                typeof languageObj[language] === 'object' &&
                (languageObj[language] as TipTapContentContentItem).type !== undefined
            ) {
                return languageObj[language] as
                    | TipTapContentContentItem
                    | TipTapParagraphContentItem;
            }

            // if the index is provided, check if the languageObj is an array and return the string at the index
            if (index !== undefined && languageObj && Array.isArray(languageObj[language])) {
                const langArray = languageObj[language] as string[];
                if (langArray[index] && langArray[index].length > 0) {
                    return langArray[index];
                }
            }

            if (typeof englishString === 'undefined')
                return 'Missing translatation for english string';

            return englishString;
        },
        [language],
    );

    const { t } = useTranslation();
    return (
        <LangContext.Provider value={{ language, setLanguage: setLanguageWithFilter, getI18n, t }}>
            <I18nextProvider i18n={i18n}>
                <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
            </I18nextProvider>
        </LangContext.Provider>
    );
}

LangContext.displayName = 'LangContext';

/**
 * Get the current language context
 * @returns {ILangContext}
 */
export function useLang(): ILangContext {
    return useContext(LangContext);
}
